/**
 * Read adapters for analyst charts stored before the plot contract (plots plan T).
 *
 * A chart built since T carries Python's `plot` and is drawn as is. A stored
 * `analyst-chart.v1` spec without one is mapped by its shape onto a plot kind
 * so the tab draws every chart through the same kind views. Shape only: the
 * stored values are copied as they are (a stored donut draws its stated
 * shares, never shares renormalised over the visible slices), nothing is
 * crowned or rounded here (a ranking is ordered by its stored score, as
 * today), and suppressed categories are left out (the host lists them).
 * Stored charts carry no chrome, so the host passes the plot label keys in
 * the viewer's UI language (`AnalystLegacyPlotLabels`).
 *
 * | Stored | Kind |
 * |---|---|
 * | `ranking` | ranking-bars (stored scores highest first, dense ranks on the value as read) |
 * | percent format, one series, `bar` or `donut` | choice-bars |
 * | >= 4 series x >= 4 categories (`bar` / `stacked_bar`), one format with a scale | heat-grid with heat (the dense cut, Python's rule) |
 * | percent format, two or more series, `bar` | grouped-bars |
 * | anything else | heat-grid without heat (a plain table) |
 *
 * The dense cut's heat is the featured exhibits' stored read (legacy reader
 * item 7, moved here from `featuredPlotMappers`): a share over 100, a mean
 * over its stamped rating unit's bounds. Charts built since T carry Python's
 * heat instead.
 */

import {
  AnalystChartPlot,
  AnalystChartSeries,
  AnalystChartSpec,
  AnalystValueFormat,
  RatingUnitId,
  ratingBounds,
} from '@digital-twins/shared-types'

import {
  DENSE_CUT_MIN_CUTS,
  DENSE_CUT_MIN_GROUPS,
  isDrawableAnalystPlot,
} from '@/utils/analystChartPlot'

/**
 * `chartType` is an alias of `plot.kind` (held equal to Python's
 * `chart_plot.CHART_TYPE_BY_KIND` by test_analyst_chart_type_parity.py).
 */
export const ANALYST_CHART_TYPE_BY_PLOT_KIND: Record<string, AnalystChartSpec['chartType']> = {
  'choice-bars': 'bar',
  'share-donut': 'donut',
  'ranking-bars': 'ranking',
  'grouped-bars': 'bar',
  'stacked-100-bars': 'stacked_bar',
  'diverging-likert': 'stacked_bar',
  'heat-grid': 'bar',
  'density-histogram': 'histogram',
}

/** The chrome a legacy read prints, keyed by the plot label keys, in the viewer's UI language. */
export interface AnalystLegacyPlotLabels {
  plot_no_data: string
  plot_low_base: string
  too_close_to_call: string
  unsupported_rating_scale: string
}

/** A rank number template that reads the same in every language. */
const RANK_TEMPLATE = '#{rank}'

const PALETTE_SLOTS = 8

function slot(position: number): number {
  return (position % PALETTE_SLOTS) + 1
}

function isDrawn(datum: AnalystChartSeries['data'][number]): boolean {
  return !datum.isSuppressed && datum.value !== null
}

function datumLabel(datum: AnalystChartSeries['data'][number]): string {
  return datum.categoryFull ?? datum.category
}

function choiceBars(chart: AnalystChartSpec, labels: AnalystLegacyPlotLabels): AnalystChartPlot {
  const series = chart.series[0]
  const drawn = series.data.map((datum, index) => ({ datum, index })).filter(({ datum }) => isDrawn(datum))
  return {
    kind: 'choice-bars',
    data: {
      basis: 'respondents',
      multiSelect: false,
      series: [
        {
          id: series.id,
          label: null,
          base: null,
          lowBase: false,
          items: drawn.map(({ datum, index }) => ({
            id: `d${index}`,
            label: datumLabel(datum),
            share: datum.value,
            role: 'option',
            colorSlot: slot(index),
          })),
          // A stored chart never crowned a winner; nothing is inferred.
          topIds: [],
          tie: false,
        },
      ],
      decimals: 1,
      labels: { plot_no_data: labels.plot_no_data },
    },
  }
}

function groupedBars(chart: AnalystChartSpec, labels: AnalystLegacyPlotLabels): AnalystChartPlot {
  const categories = chart.series[0].data.map((datum, index) => ({
    id: `c${index}`,
    label: datumLabel(datum),
    role: 'option',
    colorSlot: slot(index),
  }))
  return {
    kind: 'grouped-bars',
    data: {
      valueUnit: 'percent',
      basis: 'respondents',
      // Stored shares carry no select mode: never drawn as parts of one whole.
      multiResponse: true,
      categories,
      rows: chart.series.map((series, rowIndex) => ({
        id: series.id,
        label: series.label,
        kind: 'segment',
        colorSlot: slot(rowIndex),
        base: null,
        lowBase: false,
        bars: series.data
          .map((datum, index) => ({ datum, index }))
          .filter(({ datum }) => isDrawn(datum))
          .map(({ datum, index }) => ({ categoryId: `c${index}`, share: datum.value })),
        topCategoryIds: [],
      })),
      decimals: 1,
      labels: { plot_low_base: labels.plot_low_base },
    },
  }
}

function rankingBars(chart: AnalystChartSpec, labels: AnalystLegacyPlotLabels): AnalystChartPlot {
  const series = chart.series[0]
  // Today's ranking read: the stored scores, highest first (stable on ties),
  // dense ranks on the stored value as read (no rounding).
  const drawn = series.data
    .map((datum, index) => ({ datum, index }))
    .filter(({ datum }) => isDrawn(datum))
    .sort((left, right) => (right.datum.value as number) - (left.datum.value as number))
  let rank = 0
  let previous: number | null = null
  const items = drawn.map(({ datum, index }) => {
    const value = datum.value as number
    if (previous === null || value !== previous) {
      rank += 1
      previous = value
    }
    return {
      id: `d${index}`,
      label: datumLabel(datum),
      value,
      rank,
      lowBase: false,
      base: datum.baseN,
      colorSlot: slot(index),
    }
  })
  return {
    kind: 'ranking-bars',
    data: {
      items,
      // Today's bar track: the drawn item count, the scale the survey-tab
      // ranking rows used when no scale was stamped.
      scale: { min: 0, max: items.length },
      unit: 'weighted_score',
      figure: 'weighted_score',
      decimals: 1,
      topIds: [],
      tie: false,
      basis: 'respondents',
      labels: { plot_rank_n: RANK_TEMPLATE, too_close_to_call: labels.too_close_to_call },
    },
  }
}

/** The plot column format of a stored series, from its typed format (`formatAnalystValue`'s rule). */
function plotFormat(format: AnalystValueFormat): { valueFormat: string; decimals: number } {
  if (format === 'percent') {
    return { valueFormat: 'percent', decimals: 1 }
  }
  if (format === 'count') {
    return { valueFormat: 'count', decimals: 0 }
  }
  if (format === 'mean') {
    return { valueFormat: 'mean', decimals: 2 }
  }
  return { valueFormat: 'index', decimals: 2 }
}

/** A stored value's heat scale from its typed unit, never from the values; null without one. */
export interface LegacyHeatScale {
  heat: (value: number) => number
  column: Record<string, unknown>
}

/**
 * The stored heat read, by the typed format: a share over 0-100, a mean over
 * its stamped rating unit's bounds (`scaleUnit`). Anything else (counts,
 * unscaled means) has no scale: null.
 */
export function legacyHeatScale(
  format: AnalystValueFormat,
  scaleUnit: RatingUnitId | undefined,
): LegacyHeatScale | null {
  if (format === 'percent') {
    return {
      heat: (value) => clampUnit(value / 100),
      column: { valueFormat: 'percent', decimals: 1, min: 0, max: 100 },
    }
  }
  if (format === 'mean' && scaleUnit) {
    const [min, max] = ratingBounds(scaleUnit)
    if (!(max > min)) {
      return null
    }
    return {
      heat: (value) => clampUnit((value - min) / (max - min)),
      column: { valueFormat: 'mean', decimals: 2, unit: scaleUnit, min, max, polarity: 'higher_is_better' },
    }
  }
  return null
}

function clampUnit(value: number): number {
  return Math.min(1, Math.max(0, value))
}

function isDenseCut(chart: AnalystChartSpec): boolean {
  return (
    (chart.chartType === 'bar' || chart.chartType === 'stacked_bar') &&
    chart.series.length >= DENSE_CUT_MIN_GROUPS &&
    chart.series[0].data.length >= DENSE_CUT_MIN_CUTS
  )
}

/** A stored dense cut as a heat grid with the stored heat read, or null when its unit has no scale. */
function denseHeatGrid(chart: AnalystChartSpec, labels: AnalystLegacyPlotLabels): AnalystChartPlot | null {
  const first = chart.series[0]
  if (
    chart.series.some(
      (series) => series.valueFormat !== first.valueFormat || series.scaleUnit !== first.scaleUnit,
    )
  ) {
    return null
  }
  const scale = legacyHeatScale(first.valueFormat, first.scaleUnit)
  if (!scale) {
    return null
  }
  const columns = first.data.map((datum, index) => ({
    id: `c${index}`,
    label: datumLabel(datum),
    kind: 'measure',
    ...scale.column,
  }))
  return {
    kind: 'heat-grid',
    data: {
      status: 'ok',
      columns,
      rows: chart.series.map((series) => ({
        id: series.id,
        label: series.label || series.id,
        kind: 'segment',
        base: storedRowBase(series.data.filter(isDrawn).map((datum) => datum.baseN)),
        cells: series.data.map((datum, index) => {
          const value = isDrawn(datum) ? (datum.value as number) : null
          return { columnId: `c${index}`, value, heat: value === null ? null : scale.heat(value) }
        }),
      })),
      labels: plainGridLabels(labels),
    },
  }
}

/** A stored row's base: its drawn cells' largest stored base (Python's table rule), null without one. */
export function storedRowBase(bases: Array<number>): number | null {
  const stored = bases.filter((base) => Number.isInteger(base) && base >= 0)
  return stored.length > 0 ? Math.max(...stored) : null
}

function plainGridLabels(labels: AnalystLegacyPlotLabels): Record<string, string> {
  return { unsupported_rating_scale: labels.unsupported_rating_scale, plot_low_base: labels.plot_low_base }
}

function plainGrid(chart: AnalystChartSpec, labels: AnalystLegacyPlotLabels): AnalystChartPlot {
  const categories = chart.series[0].data.map((datum) => datumLabel(datum))
  if (chart.series.length === 1) {
    const series = chart.series[0]
    const format = plotFormat(series.valueFormat)
    const column = { id: 'value', label: series.label, kind: 'measure', ...format }
    return {
      kind: 'heat-grid',
      data: {
        status: 'ok',
        columns: [column],
        rows: series.data
          .map((datum, index) => ({ datum, index }))
          .filter(({ datum }) => isDrawn(datum))
          .map(({ datum, index }) => ({
            id: `d${index}`,
            label: datumLabel(datum),
            kind: 'item',
            cells: [{ columnId: 'value', value: datum.value, heat: null }],
          })),
        labels: plainGridLabels(labels),
      },
    }
  }
  const columns = categories.map((label, index) => ({
    id: `c${index}`,
    label,
    kind: 'measure',
    ...plotFormat(chart.series[0].valueFormat),
  }))
  return {
    kind: 'heat-grid',
    data: {
      status: 'ok',
      columns,
      rows: chart.series.map((series) => ({
        id: series.id,
        label: series.label,
        kind: 'segment',
        cells: series.data.map((datum, index) => ({
          columnId: `c${index}`,
          value: isDrawn(datum) ? datum.value : null,
          heat: null,
        })),
      })),
      labels: plainGridLabels(labels),
    },
  }
}

/** A stored analyst chart without `plot`, as a plot kind (shape only). */
export function legacyAnalystChartPlot(chart: AnalystChartSpec, labels: AnalystLegacyPlotLabels): AnalystChartPlot {
  const percent = chart.series[0]?.valueFormat === 'percent'
  if (chart.chartType === 'ranking' && chart.series.length === 1) {
    return rankingBars(chart, labels)
  }
  if (percent && chart.series.length === 1 && (chart.chartType === 'bar' || chart.chartType === 'donut')) {
    return choiceBars(chart, labels)
  }
  if (isDenseCut(chart)) {
    const dense = denseHeatGrid(chart, labels)
    if (dense) {
      return dense
    }
  }
  if (percent && chart.series.length >= 2 && chart.chartType === 'bar') {
    return groupedBars(chart, labels)
  }
  return plainGrid(chart, labels)
}

/** What the tab draws for a chart: Python's plot, else the legacy read. */
export function analystChartPlot(
  chart: AnalystChartSpec,
  labels: AnalystLegacyPlotLabels,
): { plot: AnalystChartPlot; isLegacy: boolean } {
  if (isDrawableAnalystPlot(chart.plot)) {
    return { plot: chart.plot, isLegacy: false }
  }
  return { plot: legacyAnalystChartPlot(chart, labels), isLegacy: true }
}
